import apiStoresBOBFF from 'src/config/api/apiStoreBFF';
import { ITransactionsResponse, ITransactionDetailResponse, ITransactionsSummary, ITransactionsSummaryResponse, ITransactionsMetadataResponse, IKpisApiResponse, ISearchTransactionsCriteria } from '@interfaces/transactions';
import { IServiceParams } from '@interfaces/services';

import {
  TRANSACTION_PATH,
  EXPORT_TRANSACTION_PATH,
  EXPORT_TYPE_CSV,
  SOURCE_BO,
  loadUrlParamsFromObject,
  TRANSACTION_PATH_V2,
  SUMMARY_TRANSACTION_PATH,
  METADATA_TRANSACTION_PATH,
  SEARCH_TRANSACTION_PATH
} from '../../utils';

const findAll = async ({ filterUrlParams, pagination = { page: 1, limit: 20 } }: IServiceParams) => {
  const filterParams = loadUrlParamsFromObject(filterUrlParams);
  const paginationParams = loadUrlParamsFromObject(pagination);
  const url = `${TRANSACTION_PATH_V2}?source=${SOURCE_BO}&${paginationParams}&${filterParams}`;
  const response = await apiStoresBOBFF.get<ITransactionsResponse>(url);
  return response.data;
};

const findById = async (id: string) => {
  const url = `${TRANSACTION_PATH}/${id}`;
  const response = await apiStoresBOBFF.get<ITransactionDetailResponse>(url);
  return response.data;
};

/** El endpoint devuelve el CSV ya armado (text/csv), no filas para componer. */
const exportTransactions = async (filterUrlParams: Object): Promise<string> => {
  const filterParams = loadUrlParamsFromObject(filterUrlParams);
  const url = `${EXPORT_TRANSACTION_PATH}?source=${SOURCE_BO}&${filterParams}&export_type=${EXPORT_TYPE_CSV}`;
  const response = await apiStoresBOBFF.get<string>(url, { responseType: 'text' });
  return response.data;
};

/** Los montos del BFF vienen en centavos; el portal muestra pesos. */
/** Las tasas del BFF vienen como porcentaje (0-100); el portal usa ratio (0-1). */
const percentToRatio = (value?: number | null): number | null =>
  value === null || value === undefined ? null : value / 100;

const orNull = (value?: number | null): number | null =>
  value === null || value === undefined ? null : value;

/**
 * Traduce la respuesta de /v2/transactions/kpis al modelo del portal:
 * avg_ticket -> average_ticket, rejection_rate -> canceled_rate y porcentajes
 * a ratios. Los montos llegan en la misma unidad que el listado, asi que no se
 * convierten: dividirlos mostraba un TPV cien veces menor que la suma de la tabla.
 */
export const mapKpisToSummary = (kpis: IKpisApiResponse): ITransactionsSummary => ({
  total_payments: kpis.total_payments,
  tpv_total: orNull(kpis.tpv_total),
  average_ticket: orNull(kpis.avg_ticket),
  approval_rate: percentToRatio(kpis.approval_rate),
  canceled_rate: percentToRatio(kpis.rejection_rate)
});

const getTransactionsSummary = async (filterUrlParams: Object): Promise<ITransactionsSummaryResponse> => {
  const filterParams = loadUrlParamsFromObject(filterUrlParams);
  const url = `${SUMMARY_TRANSACTION_PATH}?source=${SOURCE_BO}&${filterParams}`;
  const response = await apiStoresBOBFF.get<IKpisApiResponse>(url);
  // El endpoint devuelve los KPIs planos, sin envoltorio `data`.
  return { data: mapKpisToSummary(response.data) };
};

const getTransactionsMetadata = async () => {
  const url = `${METADATA_TRANSACTION_PATH}?source=${SOURCE_BO}`;
  const response = await apiStoresBOBFF.get<ITransactionsMetadataResponse>(url);
  return response.data;
};

/**
 * Búsqueda exacta de un pago (H3). `amount` debe ser > 0: `loadUrlParamsFromObject`
 * descarta valores falsy, por lo que un 0 saldría silenciosamente sin el parámetro.
 */
const searchTransactions = async (criteria: ISearchTransactionsCriteria) => {
  if (!criteria.amount || criteria.amount <= 0) {
    throw new Error('searchTransactions: amount debe ser mayor a 0');
  }
  const criteriaParams = loadUrlParamsFromObject(criteria);
  const url = `${SEARCH_TRANSACTION_PATH}?source=${SOURCE_BO}&${criteriaParams}`;
  const response = await apiStoresBOBFF.get<ITransactionsResponse>(url);
  return response.data;
};

const create = async () => {
  throw new Error('Function not implemented.');
};

const update = async () => {
  throw new Error('Function not implemented.');
};

const deleteById = async (id: number) => {
  throw new Error('Function not implemented.');
};

const transactionsServices = {
  findAll,
  findById,
  create,
  update,
  deleteById,
  exportTransactions,
  getTransactionsSummary,
  getTransactionsMetadata,
  searchTransactions
};

export default transactionsServices;
